// 学習者に見せる「デッサン」画像（デッサン風の2次元画像）の正本。
//
// デッサン画像は「モデル名＋変形量（誤り0.5／中間0.2）＋既定視点＋800×600」だけで決まり、
// 学習者の操作では変わらない。そこで実行時に生成せず、事前に作った PNG を
// public/dessin/ に置いて読むだけにしている（2026-10-10変更）。理由：
//   ・学習者の端末（GPU・ブラウザ）の違いで線の出方が変わらず、全員が同じデッサンを見る
//   ・モデルの実装を触っても、作り直すまでは提示画像が変わらない（プレテストとの整合を保つ）
//   ・実験当日に画像処理サーバー（Flask）が要らない
//
// ★モデルの形を変えたら /drawing-builder（開発用）で作り直す★
//   今の実装から描いた画像と public/dessin/ の画像を並べて差分を出し、変わったものだけ保存できる。
//
// ファイル名：誤りモデルは <model>_error.png / <model>_mid.png、モチーフは <model>.png

// 誤りモデル（演習・デモ・旧画面で使う全モデル）
export const DRAWING_ERROR_MODELS = [
  'model1', 'model2', 'model3', 'model4', 'model5', 'model6', 'model7', 'model8',
  'cube_model1', 'cube_model2', 'cube_model3', 'cube_model4', 'cube_model5',
];

// モチーフ（誤りモデルに応じて useExerciseModels と同じ規則で選ぶ）
export const DRAWING_MOTIFS = ['model', 'cube'];

// 変形量。ShapeChange の3ステップ（誤り→中間→モチーフ）と同じ値
export const DRAWING_LEVELS = { error: 0.5, mid: 0.2 };

export const motifFor = (glbPath) => (glbPath && glbPath.startsWith('cube') ? 'cube' : 'model');

const isMotif = (glbPath) => DRAWING_MOTIFS.includes(glbPath);

export function drawingFileName(glbPath, level = 'error') {
  return isMotif(glbPath) ? `${glbPath}.png` : `${glbPath}_${level}.png`;
}

export function getDrawingUrl(glbPath, level = 'error') {
  return `${process.env.PUBLIC_URL || ''}/dessin/${drawingFileName(glbPath, level)}`;
}

// 形状変更の3ステップ分（並びは ShapeChange の drawingImages と同じ＝誤り・中間・モチーフ）
export function getShapeDrawingUrls(errorGlbPath) {
  return [
    getDrawingUrl(errorGlbPath, 'error'),
    getDrawingUrl(errorGlbPath, 'mid'),
    getDrawingUrl(motifFor(errorGlbPath)),
  ];
}

// 作り直しの対象一覧（/drawing-builder が使う）。shearValue は描画時の変形量
export const DRAWING_CATALOG = [
  ...DRAWING_ERROR_MODELS.flatMap((glbPath) =>
    Object.entries(DRAWING_LEVELS).map(([level, shearValue]) => ({
      glbPath, level, shearValue, fileName: drawingFileName(glbPath, level),
    }))
  ),
  ...DRAWING_MOTIFS.map((glbPath) => ({
    glbPath, level: 'motif', shearValue: null, fileName: drawingFileName(glbPath),
  })),
];

// モチーフの写真（PartSelect＝各問の X-1 で「モチーフ」の上段に出す）。
// 正本は public/motif/motif.jpg（実物を撮った写真）。そのままだと大きい（約1MB）ので、
// 長辺1200pxに縮小した motif_display.jpg を表示に使う（写真を差し替えたら縮小版も作り直す）。
// 写真が無いモチーフ（立方体）は、代わりにモチーフのデッサンを出す。
export const MOTIF_PHOTOS = {
  model: 'motif_display.jpg',
};

export function getMotifReference(motifGlbPath) {
  const photo = MOTIF_PHOTOS[motifGlbPath];
  return photo
    ? { src: `${process.env.PUBLIC_URL || ''}/motif/${photo}`, label: '写真' }
    : { src: getDrawingUrl(motifGlbPath), label: 'デッサン' };
}

/**
 * 演習で見せる画像の一覧（演習を開いたときに先読みする。utils/imageCache.js）。
 * 各問の誤りのデッサン（mid=true なら中間も）・モチーフのデッサン・部品選択で出すモチーフの写真。
 */
export function getExerciseImageUrls(modelMap, { mid = false } = {}) {
  const urls = modelMap.flatMap((glbPath) => {
    const motif = motifFor(glbPath);
    return [
      getDrawingUrl(glbPath, 'error'),
      ...(mid ? [getDrawingUrl(glbPath, 'mid')] : []),
      getDrawingUrl(motif),
      getMotifReference(motif).src,
    ];
  });
  return [...new Set(urls)];
}
